import { useState } from 'react';

import ContentAdd from '@mui/icons-material/Add';
import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { useListController, WithListContext } from 'react-admin';
import { Alert } from 'shared/mui/Alert/Alert';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { DatagridSkeleton } from 'shared/react-admin/Datagrids';
import { ListPage } from 'shared/react-admin/Pages';
import { ProviderShowDialog } from 'shared/widgets/ProviderShowDialog/ProviderShowDialog';
import { MerchantCredentials } from 'types/embedded/merchant';
import { Permission } from 'types/embedded/permission';
import { Provider } from 'types/embedded/provider';

import { MerchantsShowCredentialsCreateDialog } from './MerchantsShowCredentialsCreateDialog/MerchantsShowCredentialsCreateDialog';
import { MerchantsShowCredentialsDatagrid } from './MerchantsShowCredentialsDatagrid/MerchantsShowCredentialsDatagrid';
import { MerchantsShowCredentialsDeleteDialog } from './MerchantsShowCredentialsDeleteDialog/MerchantsShowCredentialsDeleteDialog';
import { MerchantsShowCredentialsDialog } from './MerchantsShowCredentialsDialog/MerchantsShowCredentialsDialog';
import { MerchantsShowCredentialsEditDialog } from './MerchantsShowCredentialsEditDialog/MerchantsShowCredentialsEditDialog';
import { MerchantsShowCredentialsFilters } from './MerchantsShowCredentialsFilters/MerchantsShowCredentialsFilters';
import { MerchantsShowCredentialsShowDialog } from './MerchantsShowCredentialsShowDialog/MerchantsShowCredentialsShowDialog';
import { MerchantsShowActionSkeleton } from '../components/MerchantsShowActionSkeleton';
import { MerchantsShowFiltersSkeleton } from '../components/MerchantsShowFiltersSkeleton';

type Props = {
  merchantId?: string;
  merchantName?: string;
};

export const MerchantsShowCredentials: React.FC<Props> = ({ merchantId, merchantName }) => {
  const { hasResourcePermissions } = usePermissions();
  const { isLoading } = useListController();

  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const {
    record: provider,
    handleCloseRecordModal: handleCloseRecordModalProvider,
    openRecordModal: openRecordModalProvider,
    handleOpenRecordModal: handleOpenRecordModalProvider,
  } = useRecordModal<Provider>();

  const {
    record: recordCredentialsDialog,
    handleCloseRecordModal: handleCloseCredentialsDialog,
    openRecordModal: openCredentialsDialog,
    handleOpenRecordModal: handleOpenCredentialsDialog,
  } = useRecordModal<MerchantCredentials>();

  const {
    record: recordShowDialog,
    handleCloseRecordModal: handleCloseShowDialog,
    openRecordModal: openShowDialog,
    handleOpenRecordModal: handleOpenShowDialog,
  } = useRecordModal<MerchantCredentials>();

  const {
    record: recordEditDialog,
    handleCloseRecordModal: handleCloseEditDialog,
    openRecordModal: openEditDialog,
    handleOpenRecordModal: handleOpenEditDialog,
  } = useRecordModal<MerchantCredentials>();

  const {
    record: recordDeleteDialog,
    handleCloseRecordModal: handleCloseDeleteDialog,
    openRecordModal: openDeleteDialog,
    handleOpenRecordModal: handleOpenDeleteDialog,
  } = useRecordModal<MerchantCredentials>();

  return (
    <ListPage
      actions={[
        hasResourcePermissions('merchant_credentials', [Permission.Full, Permission.Create]) &&
          (isLoading ? (
            <MerchantsShowActionSkeleton />
          ) : (
            <Button
              key="create"
              label="Добавить"
              onClick={() => setOpenCreateDialog(true)}
              startIcon={<ContentAdd />}
              variant="contained"
            />
          )),
      ]}
      disableSyncWithLocation={true}
      empty={false}
      filter={{
        merchant: merchantId,
      }}
      headerTitle={{ titleText: 'Список реквизитов доступа', titleSize: 'smallBold' }}
      resource="merchant_credentials"
      sort={{ field: 'id', order: 'DESC' }}
      sx={{ marginTop: 0 }}
    >
      <WithListContext
        render={({ data, isLoading, filterValues }) => {
          const showAlert = !data?.length && !Object.keys(filterValues)?.length && !isLoading;
          if (showAlert) {
            return (
              <Alert severity="info">Мерчанту {merchantName} не добавлены реквизиты доступа.</Alert>
            );
          }

          return (
            <>
              {isLoading ? <MerchantsShowFiltersSkeleton /> : <MerchantsShowCredentialsFilters />}
              {isLoading ? (
                <DatagridSkeleton columnsCount={10} />
              ) : (
                <MerchantsShowCredentialsDatagrid
                  handleOpenCredentialsDialog={handleOpenCredentialsDialog}
                  handleOpenDeleteDialog={handleOpenDeleteDialog}
                  handleOpenEditDialog={handleOpenEditDialog}
                  handleOpenRecordModalProvider={handleOpenRecordModalProvider}
                  handleOpenShowDialog={handleOpenShowDialog}
                />
              )}
            </>
          );
        }}
      />
      {provider && (
        <ProviderShowDialog
          onClose={handleCloseRecordModalProvider}
          open={openRecordModalProvider}
          provider={provider}
        />
      )}
      <MerchantsShowCredentialsDeleteDialog
        onClose={handleCloseDeleteDialog}
        open={openDeleteDialog}
        record={recordDeleteDialog}
      />
      <MerchantsShowCredentialsEditDialog
        merchantId={merchantId}
        merchantName={merchantName}
        onClose={handleCloseEditDialog}
        onDelete={handleOpenDeleteDialog}
        open={openEditDialog}
        record={recordEditDialog}
      />
      <MerchantsShowCredentialsCreateDialog
        merchantId={merchantId}
        merchantName={merchantName}
        onClose={() => setOpenCreateDialog(false)}
        open={openCreateDialog}
      />
      <MerchantsShowCredentialsShowDialog
        handleOpenCredentialsDialog={handleOpenCredentialsDialog}
        id={recordShowDialog?.id}
        onClose={handleCloseShowDialog}
        onDelete={handleOpenDeleteDialog}
        onEdit={handleOpenEditDialog}
        open={openShowDialog}
      />
      <MerchantsShowCredentialsDialog
        actions={
          <CardActionsButtons
            rightActionsSlot={[
              <Button
                key="back-button"
                label="К просмотру"
                onClick={() => {
                  handleCloseCredentialsDialog();
                  handleOpenShowDialog(recordCredentialsDialog);
                }}
                variant="outlined"
              />,
              hasResourcePermissions('merchant_credentials', [
                Permission.Full,
                Permission.Update,
              ]) &&
                !recordCredentialsDialog?.locked && (
                  <Button
                    key="edit-button"
                    label="Редактировать"
                    onClick={() => {
                      handleCloseCredentialsDialog();
                      handleOpenEditDialog(recordCredentialsDialog);
                    }}
                    variant="contained"
                  />
                ),
            ]}
          />
        }
        onClose={() => handleCloseCredentialsDialog()}
        open={openCredentialsDialog}
        record={recordCredentialsDialog}
      />
    </ListPage>
  );
};
